import type { ReactNode } from 'react';
import { useRef } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
  useMotionTemplate,
} from 'framer-motion';
import { cn } from '~/lib/utils';
import styles from './ScrollRevealContent.module.css';

type ContentTag = 'div' | 'p' | 'span' | 'blockquote' | 'li' | 'section';

const MOTION_TAGS = {
  div: motion.div,
  p: motion.p,
  span: motion.span,
  blockquote: motion.blockquote,
  li: motion.li,
  section: motion.section,
} as const;

export interface ScrollRevealContentProps {
  children: ReactNode;
  as?: ContentTag;
  className?: string;
}

export function ScrollRevealContent({
  children,
  as = 'div',
  className,
}: ScrollRevealContentProps) {
  const ref = useRef<HTMLElement>(null);
  const reducedMotion = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 90%', 'start 75%'],
  });

  const opacity = useTransform(scrollYProgress, [0, 1], [0.3, 1]);
  const blurPx = useTransform(scrollYProgress, [0, 1], [4, 0]);
  const filter = useMotionTemplate`blur(${blurPx}px)`;

  if (reducedMotion) {
    const Tag = as;
    return (
      <Tag
        ref={ref as React.Ref<never>}
        className={cn(styles.content, styles.reducedMotion, className)}
      >
        {children}
      </Tag>
    );
  }

  const MotionTag = MOTION_TAGS[as];

  return (
    <MotionTag
      ref={ref as React.Ref<never>}
      className={cn(styles.content, className)}
      style={{ opacity, filter }}
    >
      {children}
    </MotionTag>
  );
}

ScrollRevealContent.displayName = 'ScrollRevealContent';
